import React, { useState, useEffect } from 'react';
import { 
  X, Settings, Copy, Edit3, Trash2, Eye, Users, Calendar, 
  CheckCircle, Clock, Layers, ListChecks, FileText, TrendingUp,
  Globe, Building2, Download, UserPlus, Activity, Loader2, AlertTriangle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { Avatar } from '../ui';

/**
 * VPG Global Library Assessment Management Modal (Superuser Only)
 * Shows detailed assessment info, usage stats, and management controls
 */
const VPGManagementModal = ({ 
  assessment, 
  onClose, 
  onEdit, 
  onDelete, 
  onCopyToLibrary,
  formatDate 
}) => {
  const [activeTab, setActiveTab] = useState('overview'); // overview | usage | settings
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [usageData, setUsageData] = useState({ studies: [], users: [], changeLog: [] });
  
  // Fetch usage data when tab is selected
  useEffect(() => {
    if (activeTab === 'usage' && assessment?.id) {
      fetchUsageData();
    }
  }, [activeTab, assessment?.id]);
  
  const fetchUsageData = async () => {
    setLoadingUsage(true);
    try {
      const response = await api.get(`/api/assessment-builder/assessments/${assessment.uuid}/usage/`);
      setUsageData(response.data);
    } catch (error) {
      console.error('Error fetching usage data:', error);
    } finally {
      setLoadingUsage(false);
    }
  };
  
  // Helper to get user initials
  const getInitials = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return user.email?.substring(0, 2).toUpperCase() || 'U';
  };
  
  // Helper to get display name
  const getDisplayName = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.email?.split('@')[0] || 'Unknown User';
  };
  
  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  if (!assessment) return null;

  // Get screens data
  const screens = assessment.survey?.screens || assessment.screens || [];
  const totalScreens = screens.length;
  // Use question_count from API which counts ALL assessment items, not just items placed on screens
  const totalItems = assessment.question_count || 0;

  // Shared looks (FECOA-2174)
  const sectionTitleClass = 'text-sm font-semibold text-gray-900 dark:text-gray-50';
  const wellClass = 'rounded-lg border border-gray-200/70 bg-gray-50 dark:border-white/[0.07] dark:bg-white/[0.03]';
  const emptyClass = 'rounded-lg border border-dashed border-gray-200 py-8 text-center dark:border-white/10';
  const tabClass = (tab) => `tab ${activeTab === tab ? 'tab-active' : ''}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 pb-3 pt-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">VPG Library Management</h2>
            <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">Manage global assessment template</p>
          </div>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-sm btn-icon -mr-2"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6">
          <div className="tabs-list border-gray-100 dark:border-white/[0.06]">
            <button
              onClick={() => setActiveTab('overview')}
              className={tabClass('overview')}
            >
              <Eye className="h-4 w-4" />
              Overview
            </button>
            <button
              onClick={() => setActiveTab('usage')}
              className={tabClass('usage')}
            >
              <Users className="h-4 w-4" />
              Usage & Users
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={tabClass('settings')}
            >
              <Settings className="h-4 w-4" />
              Settings
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">

              {/* Assessment Info Card */}
              <div className="rounded-xl border border-gray-200/80 p-5 dark:border-white/[0.07]">
                <h3 className="mb-4 text-[15px] font-semibold text-gray-900 dark:text-gray-50">{assessment.name || assessment.survey?.name}</h3>

                <div className="grid grid-cols-1 gap-x-4 gap-y-3 text-[13px] sm:grid-cols-3">
                  <div className="flex items-center">
                    <span className="text-gray-500 dark:text-gray-400">Version:</span>
                    <span className="code-chip ml-2">v{assessment.version}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 dark:text-gray-400">Library:</span>
                    <span className="badge badge-gray ml-2 capitalize">
                      <Globe className="h-3 w-3" />
                      VPG Global
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 dark:text-gray-400">Status:</span>
                    <span className={`badge badge-dot ml-2 ${
                      assessment.publication_status === 'published'
                        ? 'badge-success'
                        : 'badge-warning'
                    }`}>
                      {assessment.publication_status === 'published' ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 dark:text-gray-400">Type:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-gray-50 capitalize">
                      {assessment.assessment_type?.replace('_', ' ') || 'Fixed Form'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 dark:text-gray-400">Created:</span>
                    <span className="ml-2 font-medium tabular-nums text-gray-900 dark:text-gray-50">
                      {formatDate(assessment.created_at)}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 dark:text-gray-400">Modified:</span>
                    <span className="ml-2 font-medium tabular-nums text-gray-900 dark:text-gray-50">
                      {formatDate(assessment.modified || assessment.builder_metadata?.last_saved)}
                    </span>
                  </div>
                </div>

                {assessment.description && (
                  <div className="mt-4 border-t border-gray-100 pt-4 dark:border-white/[0.06]">
                    <p className="text-[13.5px] leading-relaxed text-gray-700 dark:text-gray-300">{assessment.description}</p>
                  </div>
                )}
              </div>

              {/* Survey Structure */}
              {totalScreens > 0 && (
                <div className="rounded-xl border border-gray-200/80 p-5 dark:border-white/[0.07]">
                  <div className="mb-4 flex items-center gap-2">
                    <Layers className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <h4 className={sectionTitleClass}>Survey Structure</h4>
                  </div>

                  <div className="mb-5 grid grid-cols-2 gap-3">
                    <div className={`${wellClass} p-4`}>
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">Total Screens</span>
                        <span className="text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-50">{totalScreens}</span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Assessment pages</p>
                    </div>

                    <div className={`${wellClass} p-4`}>
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">Total Items</span>
                        <span className="text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-50">{totalItems}</span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Questions & items</p>
                    </div>
                  </div>

                  {/* Screen Breakdown */}
                  <div className="border-t border-gray-100 pt-5 dark:border-white/[0.06]">
                    <div className="mb-3 flex items-center gap-2">
                      <ListChecks className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      <h4 className={sectionTitleClass}>Screen Breakdown</h4>
                    </div>

                    <div className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200/70 dark:divide-white/[0.06] dark:border-white/[0.07]">
                      {screens.map((screen, index) => (
                        <div
                          key={screen.id || index}
                          className="flex items-center justify-between gap-3 px-3.5 py-2.5"
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <span className="code-chip flex-shrink-0 tabular-nums">
                              {screen.order_number || index + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-[13.5px] font-medium text-gray-900 dark:text-gray-50">
                                {screen.title || screen.name || `Screen ${index + 1}`}
                              </p>
                              {screen.description && (
                                <p className="mt-0.5 max-w-md truncate text-xs text-gray-500 dark:text-gray-400">
                                  {screen.description}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-shrink-0 items-center">
                            <span className="badge badge-gray tabular-nums">
                              {screen.questions?.length || screen.items?.length || 0} items
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Metadata */}
              {assessment.therapeutic_area && (
                <div className={`${wellClass} p-4`}>
                  <div className="mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <h4 className={sectionTitleClass}>Additional Information</h4>
                  </div>
                  <div className="text-[13px]">
                    <span className="text-gray-500 dark:text-gray-400">Therapeutic Area:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-gray-50">{assessment.therapeutic_area}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Usage & Users Tab */}
          {activeTab === 'usage' && (
            <div className="space-y-6">
              {loadingUsage ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
                </div>
              ) : (
                <>
                  {/* VPG Template Info */}
                  <div>
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className={sectionTitleClass}>
                        Global Template
                      </h4>
                    </div>

                    <div className={`${wellClass} p-4`}>
                      <div className="flex items-start gap-3">
                        <Globe className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                        <div>
                          <p className="mb-1 text-[13.5px] font-medium text-gray-900 dark:text-gray-50">VPG Library Template</p>
                          <p className="text-[13px] leading-relaxed text-gray-600 dark:text-gray-300">
                            This is a validated global instrument template. Sponsors can copy this
                            assessment to their library and customize it for their studies.
                          </p>
                          <div className="mt-3 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                            <span className="flex items-center gap-1.5">
                              <Copy className="h-3.5 w-3.5" />
                              Use "Copy to Library" to adopt
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Users with Access */}
                  <div>
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h4 className={sectionTitleClass}>
                          Users with Access
                        </h4>
                        {usageData.users?.length > 0 && (
                          <span className="badge badge-gray tabular-nums">{usageData.users.length}</span>
                        )}
                      </div>
                    </div>

                    {usageData.users?.length > 0 ? (
                      <div className="divide-y divide-gray-100 rounded-lg border border-gray-200/70 dark:divide-white/[0.06] dark:border-white/[0.07]">
                        {usageData.users.map((user) => (
                          <div
                            key={user.id}
                            className="flex items-center justify-between gap-3 px-3.5 py-3"
                          >
                            <div className="flex min-w-0 items-center gap-3">
                              <Avatar name={getDisplayName(user)} size="sm" />
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="truncate text-[13.5px] font-medium text-gray-900 dark:text-gray-50">{getDisplayName(user)}</p>
                                  {user.permission_level === 'creator' && (
                                    <span className="badge badge-gray">
                                      Owner
                                    </span>
                                  )}
                                </div>
                                <p className="truncate text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                              </div>
                            </div>
                            <div className="flex-shrink-0 text-right">
                              <span className="badge badge-gray">
                                {user.permission_level === 'creator' ? 'Full Admin' : user.permission_level || 'View'}
                              </span>
                              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{user.role_display || 'User'}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={emptyClass}>
                        <Users className="mx-auto mb-2 h-6 w-6 text-gray-400 dark:text-gray-500" />
                        <p className="text-[13px] text-gray-500 dark:text-gray-400">No users with explicit access</p>
                      </div>
                    )}
                  </div>

                  {/* Version History */}
                  <div>
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h4 className={sectionTitleClass}>
                          Version History
                        </h4>
                        {usageData.changeLog?.length > 0 && (
                          <span className="badge badge-gray tabular-nums">{usageData.changeLog.length}</span>
                        )}
                      </div>
                    </div>

                    {usageData.changeLog?.length > 0 ? (
                      <div className="rounded-lg border border-gray-200/70 p-4 dark:border-white/[0.07]">
                        <div className="max-h-48 space-y-4 overflow-y-auto">
                          {usageData.changeLog.map((change, index) => (
                            <div key={index} className="flex items-start gap-3">
                              <span className="code-chip mt-0.5 flex-shrink-0">
                                {change.version_number?.split('.')[0] || 'v'}
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-x-2">
                                  <p className="text-[13.5px] font-medium text-gray-900 dark:text-gray-50">{change.action}</p>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatRelativeTime(change.timestamp)}
                                  </span>
                                </div>
                                <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">
                                  {change.description}
                                </p>
                                <p className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                  <Avatar name={change.user_name} size="xs" />
                                  by {change.user_name}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className={emptyClass}>
                        <Clock className="mx-auto mb-2 h-6 w-6 text-gray-400 dark:text-gray-500" />
                        <p className="text-[13px] text-gray-500 dark:text-gray-400">No version history available</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <Settings className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <h3 className={sectionTitleClass}>Management Actions</h3>
                </div>

                <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200/80 dark:divide-white/[0.06] dark:border-white/[0.07]">
                  {/* Edit Button */}
                  <button
                    onClick={() => {
                      onEdit(assessment);
                      onClose();
                    }}
                    className="flex w-full items-center justify-between px-4 py-3.5 transition-colors hover:bg-gray-50/80 dark:hover:bg-white/[0.03]"
                  >
                    <div className="flex items-center gap-3">
                      <Edit3 className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                      <div className="text-left">
                        <div className="text-[13.5px] font-medium text-gray-900 dark:text-gray-50">Edit Assessment</div>
                        <div className="text-[12.5px] text-gray-500 dark:text-gray-400">Modify content and structure</div>
                      </div>
                    </div>
                  </button>

                  {/* Copy Button */}
                  <button
                    onClick={() => {
                      onCopyToLibrary(assessment);
                      onClose();
                    }}
                    className="flex w-full items-center justify-between px-4 py-3.5 transition-colors hover:bg-gray-50/80 dark:hover:bg-white/[0.03]"
                  >
                    <div className="flex items-center gap-3">
                      <Copy className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                      <div className="text-left">
                        <div className="text-[13.5px] font-medium text-gray-900 dark:text-gray-50">Copy to Sponsor Library</div>
                        <div className="text-[12.5px] text-gray-500 dark:text-gray-400">Distribute to sponsor organizations</div>
                      </div>
                    </div>
                  </button>

                  {/* Delete Button */}
                  <button
                    onClick={() => {
                      if (window.confirm(`Are you sure you want to delete "${assessment.name || assessment.survey?.name}"? This action cannot be undone.`)) {
                        onDelete(assessment);
                        onClose();
                      }
                    }}
                    className="flex w-full items-center justify-between px-4 py-3.5 transition-colors hover:bg-red-50/60 dark:hover:bg-red-500/[0.06]"
                  >
                    <div className="flex items-center gap-3">
                      <Trash2 className="h-4 w-4 flex-shrink-0 text-red-500 dark:text-red-400" />
                      <div className="text-left">
                        <div className="text-[13.5px] font-medium text-red-600 dark:text-red-400">Delete Assessment</div>
                        <div className="text-[12.5px] text-gray-500 dark:text-gray-400">Permanently remove from VPG library</div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="rounded-lg border border-red-200 bg-red-50 p-3.5 dark:border-red-500/30 dark:bg-red-500/10">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500 dark:text-red-400" />
                  <div>
                    <h4 className="mb-1 text-[13px] font-semibold text-red-800 dark:text-red-200">Superuser Actions Only</h4>
                    <p className="text-[12.5px] leading-relaxed text-red-700 dark:text-red-300">
                      These management controls are only visible to superusers. Changes to VPG Global Library
                      assessments affect all sponsors and studies that use these templates.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            Close
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                onCopyToLibrary(assessment);
                onClose();
              }}
              className="btn btn-primary"
            >
              <Copy className="h-4 w-4" />
              Copy to Library
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VPGManagementModal;
